<template>
  <div class="groups-view-container">
    <div class="add-group-btn-container">
      <div class="breadcrumb-wrapper">
        <BreadCrumb />
      </div>
      <div class="btn-wrapper">
        <CustomButton
          text="Descargar Layout"
          variant="primary"
          :show-icon="true"
          :icon-src="excelIcon"
          icon-alt="Descargar Layout"
          @click="handleDownloadLayout"
          :icon-width="19"
          :icon-height="21"
        />
        <CustomButton
          text="Cargar empresa"
          variant="secondary"
          :show-icon="true"
          :icon-src="addGroupIcon"
          icon-alt="Cargar empresa"
          @click="handleShowModal"
          :icon-width="25"
          :icon-height="25"
        />
      </div>
    </div>
    <div class="groups-container">
      <GroupsListView :availableAgents="availableAgents" />
    </div>
    <div v-if="showModalAddGroup" class="custom-modal-overlay">
      <div class="custom-modal">
        <div class="modal-header">
          <h2>Cargar empresa</h2>
        </div>
        <!-- select groups -->
        <div class="pay-period-radio-group select-spacing">
          <label class="radio-label">Seleccionar grupo</label>
          <CustomSelect
            id="group-select"
            v-model="selectedGroup"
            :options="availableGroups"
            :option-value="(option: unknown) => option"
            :option-label="
              (option: unknown) => {
                const group = option as GroupResponseDTO
                return `${group.descripcion} ${group.nombrePublico && group.nombrePublico !== '' ? `(${group.nombrePublico})` : ''}`
              }
            "
          />
          <span v-if="!selectedGroup" class="required-text"
            >Es necesario seleccionar un Grupo para continuar</span
          >
          <!-- Message when group exists in Osmosis -->
          <div v-if="isGroupDataLoaded" class="group-exists-message">
            Empresa ya registrada en Osmosis.
          </div>
        </div>
        <!-- select groups -->
        <!-- input public name -->
        <div class="pay-period-radio-group select-spacing">
          <label class="radio-label">Nombre público</label>
          <div :class="{ 'has-value': publicGroupName }">
            <input
              type="text"
              v-model="publicGroupName"
              class="custom-select"
              required
              :disabled="isLoadingGroupData"
              @input="publicGroupName = toUpperCase(publicGroupName)"
            />
            <span v-if="!publicGroupName" class="required-text"
              >Es necesario ingresar un Nombre público para continuar</span
            >
          </div>
        </div>
        <!-- input public name -->
        <!-- select agent -->
        <div class="pay-period-radio-group select-spacing">
          <label class="radio-label">Selecciona un agente</label>
          <CustomAutoComplete
            id="agent-select"
            v-model="selectedAgent"
            :options="availableAgents"
            :option-value="(option: unknown) => (option as AgentDTO).userName"
            :option-label="
              (option: unknown) => {
                const agent = option as AgentDTO
                return `${agent.nombre} ${agent.apPaterno} ${agent.apMaterno}`
              }
            "
            :disabled="isGroupDataLoaded || isLoadingGroupData"
            placeholder="Buscar un agente..."
          />
          <span v-if="!selectedAgent" class="required-text"
            >Es necesario seleccionar un Agente para continuar</span
          >
        </div>
        <!-- select agent -->
        <!-- import data check box -->
        <div class="import-checkbox-wrapper select-spacing">
          <label class="custom-checkbox-label">
            <input
              id="import-data"
              type="checkbox"
              v-model="importFromCore"
              class="custom-checkbox-input"
            />
            <span class="custom-checkbox-box">
              <svg
                v-if="importFromCore"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="18" height="18" rx="4" fill="#8ED13A" />
                <path
                  d="M5 9.5L8 12.5L13 7.5"
                  stroke="white"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
            <span class="custom-checkbox-text">Importar colaboradores desde Core</span>
          </label>
        </div>
        <!-- import data check box -->
        <div class="custom-modal-actions select-spacing">
          <CustomButton text="Cancelar" variant="cancel" @click="handleModalCancel" />
          <CustomButton
            text="Cargar"
            variant="secondary"
            @click="goToEditBulkLoadData"
            :disabled="!selectedGroup || !publicGroupName || !selectedAgent"
          />
        </div>
      </div>
      <CustomLoading :is-visible="isLoadingGroups" text="Cargando información..." />
    </div>

    <!-- CustomLoading for group data -->
    <CustomLoading :is-visible="isLoadingGroupData" text="Cargando datos del grupo..." />

    <CustomConfirmationModal
      :show="showModalConfirmation"
      :title="modalConfirmationTitle"
      :message="modalConfirmationMessage"
      :accept-label="modalConfirmationAcceptButtonLabel"
      @accept="handleModalConfirmationAccept(modalConfirmationAction)"
      @cancel="showModalConfirmation = false"
    />
    <ToastMessage
      v-model="showToast"
      :message="toastMessage"
      :submessage="toastSubmessage"
      :duration="10000"
      :type="toastType"
      :icon="toastIcon"
    />
  </div>
</template>

<script setup lang="ts">
import { inject, onMounted, ref, watch } from 'vue'
import GroupsListView from '../components/GroupsListView.vue'
import type { GroupResponseDTO, CompanyDto, AgentDTO } from 'commons'
import { ApiService } from '../services/apiService'
import { useBulkLoadStore } from '../stores/bulkLoad'
import { useRouter } from 'vue-router'
import CustomConfirmationModal from '../components/CustomConfirmationModal.vue'
import BreadCrumb from '../components/BreadCrumb.vue'
import errorIcon from '@/assets/error-icon-white.svg'
import type { ApiError } from '@/types/errors'
import ToastMessage from '../components/ToastMessage.vue'
import { WebEnvironmentConfigService } from '../services/env-config.service'
import CustomLoading from '../components/CustomLoading.vue'
import CustomButton from '@/components/CustomButton.vue'
import addGroupIcon from '@/assets/people-icon.svg'
import excelIcon from '@/assets/excel-icon-black.svg'
import CustomSelect from '../components/CustomSelect.vue'
import CustomAutoComplete from '../components/CustomAutoComplete.vue'
import { trackGroupEvent } from '@/utils/googleAnalyticsEventsSender'

const showModalAddGroup = ref(false)
const selectedGroup = ref<GroupResponseDTO | null>(null)
const selectedAgent = ref('')
const publicGroupName = ref('')
const availableGroups = ref<GroupResponseDTO[]>([])
const availableAgents = ref<AgentDTO[]>([])
const isGroupDataLoaded = ref(false)
const isLoadingGroupData = ref(false)
const defaultAgentKey = ref<string>('')
const apiService = inject<ApiService>('apiService')
const bulkLoadStore = useBulkLoadStore()
const router = useRouter()
const showModalConfirmation = ref(false)
const modalConfirmationTitle = ref('')
const modalConfirmationMessage = ref('')
const modalConfirmationAcceptButtonLabel = ref('')
const modalConfirmationAction = ref<() => void>(() => {})
let companyDto: CompanyDto
const showToast = ref(false)
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const toastIcon = ref<string | undefined>(undefined)
const isLoadingGroups = ref(false)
const importFromCore = ref(true)

const envConfig = new WebEnvironmentConfigService()

if (!apiService) {
  throw new Error('apiService is not provided')
}

onMounted(async () => {
  selectedGroup.value = null
  selectedAgent.value = ''
  isGroupDataLoaded.value = false
  isLoadingGroupData.value = false
  bulkLoadStore.setSelectedGroup({} as GroupResponseDTO)
  bulkLoadStore.setSelectedAgent('')
  fetchAvailableAgents()
  defaultAgentKey.value = await envConfig.getDefaultGeneralAgentKey()
})

// Watch for group selection changes
watch(selectedGroup, async (newGroup) => {
  if (newGroup) {
    isLoadingGroupData.value = true
    publicGroupName.value = newGroup.nombrePublico || ''

    try {
      // Add delay for testing purposes
      await new Promise((resolve) => setTimeout(resolve, 2000)) // 2 second delay

      const groupDetails = await apiService.getGroup(newGroup.id.toString())

      if (groupDetails.agentKey) {
        selectedAgent.value = groupDetails.agentKey
      } else {
        selectedAgent.value = defaultAgentKey.value
      }

      isGroupDataLoaded.value = !!(groupDetails.paymentFrequency || groupDetails.agentKey)
    } catch (error) {
      console.error('Hubo un error: Error fetching group details ', error)
      isGroupDataLoaded.value = false
    } finally {
      isLoadingGroupData.value = false
    }
  } else {
    publicGroupName.value = ''
    selectedAgent.value = ''
    isGroupDataLoaded.value = false
  }
})

function toUpperCase(value: string) {
  return value.toUpperCase()
}

const fetchAvailableGroups = async () => {
  try {
    availableGroups.value = await apiService.getGroups()
  } catch (e) {
    console.error('Hubo un error: Error fetching groups ', e)
    availableGroups.value = []
  }
}

const fetchAvailableAgents = async () => {
  try {
    availableAgents.value = await apiService.getAgents()
    bulkLoadStore.setAvailableAgents(availableAgents.value)
  } catch (e) {
    console.error('Hubo un error: Error fetching agents ', e)
    availableAgents.value = []
  }
}

const handleShowModal = async () => {
  // Evento GA click botón cargar empresa
  trackGroupEvent('click_cargar_empresa', { location: 'GroupsView' })
  selectedGroup.value = null
  selectedAgent.value = ''
  publicGroupName.value = ''
  isGroupDataLoaded.value = false
  importFromCore.value = true // Siempre marcado por defecto
  showModalAddGroup.value = true
  isLoadingGroups.value = true
  await fetchAvailableGroups()
  isLoadingGroups.value = false
}

const handleModalCancel = () => {
  showModalAddGroup.value = false
  isGroupDataLoaded.value = false
}

const goToEditBulkLoadData = async () => {
  companyDto = await apiService.getCompanyByBusinessId(selectedGroup.value!.id.toString())
  if (companyDto) {
    const nombrePublico = selectedGroup.value?.nombrePublico
    showModalAddGroup.value = false
    const descripcion = selectedGroup.value?.descripcion
    const nombrePublicoText = nombrePublico && nombrePublico !== '' ? ` (${nombrePublico})` : ''
    modalConfirmationTitle.value = 'Error al cargar empresa'
    modalConfirmationMessage.value = `"${descripcion}${nombrePublicoText}" ya se encuentra registrado en Osmosis, ¿Quieres ver la información de la empresa existente?`
    modalConfirmationAcceptButtonLabel.value = 'Ver empresa existente'
    modalConfirmationAction.value = goToCompanyMembers
    showModalConfirmation.value = false
    bulkLoadStore.setExecuteMigration(false)
    goToCompanyMembers()
    /* showModalConfirmation.value = true */
  } else {
    try {
      await apiService.updateCoreGroup(
        selectedGroup.value!.id.toString(),
        publicGroupName.value,
        selectedGroup.value!.descripcion,
      )
      bulkLoadStore.setSelectedGroup(selectedGroup.value!)
      bulkLoadStore.setPublicGroupName(publicGroupName.value)
      console.log('selectedAgent.value!', selectedAgent.value)
      bulkLoadStore.setSelectedAgent(selectedAgent.value)
      bulkLoadStore.setExecuteMigration(importFromCore.value)
      router.push({ name: 'edit-bulk-load-data' })
    } catch (error) {
      const apiError = error as ApiError
      const errorData = await apiError.response.json()
      toastMessage.value = 'Error'
      toastSubmessage.value = errorData.message
      toastType.value = 'error'
      toastIcon.value = errorIcon
      showToast.value = true
    }
  }
}

function handleModalConfirmationAccept(action: () => void) {
  showModalConfirmation.value = false
  action()
}

const goToCompanyMembers = async () => {
  bulkLoadStore.setSelectedGroup(selectedGroup.value!)
  bulkLoadStore.setPublicGroupName(companyDto.name || '')
  console.log('selectedAgent.value!', selectedAgent.value)
  bulkLoadStore.setSelectedAgent(selectedAgent.value!)
  bulkLoadStore.setAvailableAgents(availableAgents.value)
  router.push({ name: 'edit-bulk-load-data' })
}

const handleDownloadLayout = () => {
  try {
    const link = document.createElement('a')
    link.href = '/bulkLoadTemplate.xlsx'
    link.download = 'bulkLoadTemplate.xlsx'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } catch (error) {
    console.error('Error al descargar el layout:', error)
  }
}
</script>

<style scoped>
.groups-view-container {
  width: 100%;
}

.groups-container {
  padding: 40px;
  text-align: center;
  margin-top: 1px;
}

.add-group-btn {
  background: #003896;
  color: #fff;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 16px;
  gap: 4px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 24px;
  min-width: 180px;
  height: 48px;
}

.select-spacing {
  margin-top: 30px;
}

.add-group-btn-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 24px 0 0;
}

.breadcrumb-wrapper {
  display: flex;
  align-items: center;
}

.breadcrumb-wrapper :deep(.breadcrumb) {
  margin-top: 0 !important;
  margin-left: 45px;
}

.btn-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pay-period-radio-group {
  margin-top: 30px;
}

.radio-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #2b2a2a;
  text-align: left;
}

.radio-options {
  display: flex;
  gap: 16px;
}

.radio-option {
  display: flex;
  align-items: center;
  border: 2px solid #d3d3d3;
  border-radius: 12px;
  padding: 8px 24px;
  cursor: pointer;
  background: #fff;
  transition:
    border 0.2s,
    background 0.2s;
  font-size: 1.1rem;
  position: relative;
  color: #555;
}

.radio-option.selected {
  border-color: #8cd211;
  background: #f7fef5;
}

.radio-input {
  display: none;
}

.radio-input:disabled + .custom-radio {
  opacity: 0.5;
  cursor: not-allowed;
}

.radio-input:disabled ~ span {
  opacity: 0.5;
}

.custom-radio {
  display: inline-block;
  width: 22px;
  height: 22px;
  border: 2px solid #8cd211;
  border-radius: 50%;
  margin-right: 8px;
  background: #fff;
  position: relative;
}

.radio-option.selected .custom-radio {
  background: #f7fef5;
}

.radio-input:checked + .custom-radio::after {
  content: '';
  display: block;
  width: 12px;
  height: 12px;
  background: #8cd211;
  border-radius: 50%;
  position: absolute;
  top: 4px;
  left: 4px;
}

.custom-select:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.modal-header {
  width: 200px;
  height: 59px;
  background: #ffffff;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 2;
}

.group-exists-message {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  color: #856404;
  font-size: 14px;
  font-weight: 500;
}

:deep(.custom-modal) {
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
/* Checkbox personalizado para 'Importar colaboradores desde Core' */
.import-checkbox-wrapper {
  display: flex;
  align-items: center;
  margin-top: 24px;
}
.custom-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 16px;
  user-select: none;
}
.custom-checkbox-input {
  display: none;
}
.custom-checkbox-box {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid #8ed13a;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  transition:
    background 0.2s,
    border 0.2s;
}
.custom-checkbox-input:checked + .custom-checkbox-box {
  background: #8ed13a;
  border-color: #8ed13a;
}
.custom-checkbox-text {
  color: #222;
  font-size: 16px;
}
</style>
